import * as React from "react";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { PensionArrangementType } from "../../../../../../../Wrap/Constants/Enums/Domain/PensionArrangementType";
import { CappedFlexiSection } from "../../../Common/SIPP/IncomePaymentDetails/CappedFlexiSection";
import { Arrangements } from "../../../../../../../Wrap/Models/Domains/Drawdown/Arrangements";
import { PensionContribution } from "../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContribution";
import { FlexiModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/FlexiModel";
import { calculateNetDrawdownValue } from "../../../Common/SIPP/DrawdownDetails/Helper/DrawdownDetailHelper";
import { CappedModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/CappedModel";
import { DrawdownIncomeAmendmentInstruction } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownIncomeAmendmentInstruction";
import { findIndex } from "lodash";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { setValueByKey } from "../../../../../../Shared/Table/Utils/TableUtils";
import { processTypeEnum } from "../../../Common/Resource/Contents";

interface IIncomeFromPensionProps extends IProcessComponentProps {
    data: PensionContribution;
    processType?: processTypeEnum;
    onChange: (changes) => void;
}
interface IIncomeFromPensionState {}

export class IncomeFromPension extends ProcessComponent<IIncomeFromPensionProps, IIncomeFromPensionState> {
    constructor(props: any) {
        super(props);
    }
    private handleChange(changes, key?: string) {
        const { data, onChange } = this.props;
        let _data = JSON.parse(JSON.stringify(data));
        // changes = {Capped/Felxi: []}
        const existedArrangements = data?.IncomeAmendment ? JSON.parse(JSON.stringify(data?.IncomeAmendment)) : [];
        const drawdown = data?.DrawdownModel ? JSON.parse(JSON.stringify(data?.DrawdownModel)) : null;
        if (changes?.CappedModels) {
            // handle capped model change
            changes?.CappedModels?.forEach((e: CappedModel) => {
                if (e?.From == "Crystallisation") {
                    // From drawdown
                    // TODO - handle drawdownIncome
                    this.handleDrawdownIncomeForCapped(drawdown, e);
                } else {
                    // From existed arrangement
                    this.handleCappedChange(existedArrangements, e);
                }
            });
        }
        if (changes?.FlexiModels) {
            // handle flexi model change
            changes?.FlexiModels?.forEach((e: FlexiModel) => {
                if (e?.From == "Crystallisation") {
                    // From drawdown
                    // TODO - handle drawdownIncome
                    this.handleDrawdownIncomeForFlexi(drawdown, e);
                } else {
                    // From existed arrangement
                    this.handleFlexiChange(existedArrangements, e);
                }
            });
        }

        // set changed object to data
        _data = setValueByKey(_data, "IncomeAmendment", existedArrangements);
        _data = setValueByKey(_data, "DrawdownModel", drawdown);

        onChange(_data);
    }

    private handleCappedChange(existedArrangements: Array<DrawdownIncomeAmendmentInstruction>, changes: CappedModel) {
        // Find matched arrangement id from existedArrangement
        const index = findIndex(existedArrangements, { ArrangementId: changes?.Id });
        existedArrangements[index]["CappedArrangement"] = changes;
    }

    private handleFlexiChange(existedArrangements: Array<DrawdownIncomeAmendmentInstruction>, changes: FlexiModel) {
        // Find matched arrangement id from existedArrangement
        const index = findIndex(existedArrangements, { ArrangementId: changes?.Id });
        existedArrangements[index]["FlexiArrangement"] = changes;
    }

    private handleDrawdownIncomeForCapped(drawdown: DrawdownModel, changes: CappedModel) {
        drawdown.Income.CurrentYearIncome = {
            Amount: changes?.IncomePerPaymentForCurrentPensionYear,
            Currency: CurrencyCode.GBP,
        };
        drawdown.Income.FutureYearIncome = {
            Amount: changes?.IncomePerPaymentForFuturePensionYears,
            Currency: CurrencyCode.GBP,
        };
    }

    private handleDrawdownIncomeForFlexi(drawdown: DrawdownModel, changes: FlexiModel) {
        drawdown.Income.IncomePerPayment = { Amount: changes?.IncomePerPayment, Currency: CurrencyCode.GBP };
    }

    private onIncomeChange(changes, key?: string) {
        const { data, onChange } = this.props;
        this.onDataChange(
            (changes) => {
                this.onDataChange(onChange, changes, data, "DrawdownModel");
            },
            changes,
            data?.DrawdownModel,
            key
        );
    }

    public render(): JSX.Element {
        const { data, processType } = this.props;

        const arrangements: Arrangements = {
            CappedModels: [],
            FlexiModels: [],
        };

        const existedArrangements = data?.IncomeAmendment || [];
        if (data?.DrawdownModel?.DrawdownType === DrawdownType.Flexible) {
            const drawdownModel = data.DrawdownModel;

            const drawdownTransfer: FlexiModel = {
                From: "Crystallisation",
                Amount: calculateNetDrawdownValue(drawdownModel, data?.PensionProtection),
                Type: "Member",
                IncomePerPayment: drawdownModel?.Income?.IncomePerPayment?.Amount || 0,
            };
            arrangements.FlexiModels.push(drawdownTransfer);
        }

        if (existedArrangements.length > 0) {
            existedArrangements.forEach((e) => {
                if (e?.Type == PensionArrangementType.Capped) {
                    // Capped
                    arrangements.CappedModels.push(e.CappedArrangement);
                }
                if (e?.Type == PensionArrangementType.Fad) {
                    arrangements.FlexiModels.push(e.FlexiArrangement);
                }
            });
        }

        return (
            <>
                {data?.DrawdownModel.Income?.TargetSpecificArrangements && (
                    <CappedFlexiSection
                        showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                        onRef={(ref) => this.appendChild(ref)}
                        income={data?.DrawdownModel?.Income}
                        onIncomeChange={(changes) => {
                            {
                                this.onIncomeChange(changes, "Income");
                            }
                        }}
                        data={arrangements}
                        drawdownType={data?.DrawdownModel?.DrawdownType}
                        onChange={(changes) => {
                            this.handleChange(changes, "ExistingArrangements");
                        }}
                        processType={processType}
                        modelData={data?.DrawdownModel}
                        pensionProtection={data?.PensionProtection}
                    />
                )}
            </>
        );
    }
}

export default IncomeFromPension;
